import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Formik } from "formik";
import * as yup from "yup";
import api from 'state/axios';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    TextField,
    Button,
    FormControlLabel, 
    Checkbox, 
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from "state/constants";
import { parseUTCDate } from 'state/helpers';
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import CustomDatePicker from "components/CustomDatePicker";
import Header from "components/Header";
import StyledButton from 'components/StyledButton';
import ApplicableTo from "./ApplicableTo";


const TravelAllowancePage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const { travelAllowanceId } = useParams();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [jobGrades, setJobGrades] = useState([]);
    const [formValues, setFormValues] = useState({
        travelAllowanceCode: "",
        travelAllowanceName: "",        
        variableName: "",
        effectiveFrom: new Date(),
        effectiveTo: null,
        sortOrder: 0,
        isActive: true,
        applicableTo: [],
    });


    const fetchJobGrades = useCallback(async () => {
        return await api.get('/jobgrades/active');
    }, []);
    
    const fetchTravelAllowance = useCallback(async () => {
        if (travelAllowanceId) {
            const url = `/travelallowances/${travelAllowanceId}`;
            return await api.get(url);            
        }
    }, [travelAllowanceId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [jobGradesResponse,
                    travelAllowanceResponse
                ] = await Promise.all([
                    fetchJobGrades(),
                    travelAllowanceId ? fetchTravelAllowance() : Promise.resolve(null),
                ]);
                
                setJobGrades(jobGradesResponse.data);        
                
                const formData = travelAllowanceResponse?.data || {};             
                setFormValues({
                    travelAllowanceCode: formData.travelAllowanceCode || "",
                    travelAllowanceName: formData.travelAllowanceName || "",                    
                    variableName: formData.variableName || "",
                    effectiveFrom: formData.effectiveFrom ? new Date(formData.effectiveFrom) : null,
                    effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo) : null,
                    sortOrder: formData.sortOrder || 0,
                    isActive: formData.isActive ?? true,
                    applicableTo: formData.applicableTo || [],
                });
                
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch data!");
                setError(error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchJobGrades, 
        fetchTravelAllowance, 
        travelAllowanceId
    ]); 
    
    const checkoutSchema = yup.object().shape({
        travelAllowanceCode: yup.string().required("Required"),
        travelAllowanceName: yup.string().required("Required"),
    });

    const handleFormSubmit = async (values) => {
        try {
            const payload = {
                ...values,
                effectiveFrom: values.effectiveFrom
                    ? parseUTCDate(values.effectiveFrom) 
                    : null,
                effectiveTo: values.effectiveTo
                    ? parseUTCDate(values.effectiveTo) 
                    : null,
            };
            
            const response = await api({
                method: travelAllowanceId ? "PUT" : "POST",
                url: travelAllowanceId
                    ? `/travelallowances/update/${travelAllowanceId}`
                    : '/travelallowances/new',
                data: payload,
            });

            if (![200, 201].includes(response.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            toast.success(
                travelAllowanceId
                    ? "Travel allowance updated successfully."
                    : "Travel allowance created successfully."
            );

            navigate("/travelallowances");
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to submit the form!");
        }
    };

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Travel Allowance"
                    subtitle={`${loading ? "Loading..."
                        : travelAllowanceId
                            ? "Update"
                            : "Create"
                    }`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/travelallowances")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <Typography color="error">Error: {error?.message || ''}</Typography>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: "auto" }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        gap={2}
                                        sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
                                    >
                                        <TextField
                                            label="Travel Allowance Code"
                                            name="travelAllowanceCode"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.travelAllowanceCode}
                                            error={
                                                !!touched.travelAllowanceCode &&
                                                !!errors.travelAllowanceCode
                                            }
                                            helperText={
                                                touched.travelAllowanceCode &&
                                                errors.travelAllowanceCode
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            label="Travel Allowance Name"
                                            name="travelAllowanceName"
                                            fullWidth
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.travelAllowanceName}
                                            error={
                                                !!touched.travelAllowanceName &&
                                                !!errors.travelAllowanceName
                                            }
                                            helperText={
                                                touched.travelAllowanceName &&
                                                errors.travelAllowanceName
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <CustomDatePicker
                                            label="Effective From"
                                            name="effectiveFrom"
                                            value={values.effectiveFrom}
                                            maxDate={values.effectiveTo}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <CustomDatePicker
                                            label="Effective To"
                                            name="effectiveTo"
                                            value={values.effectiveTo}
                                            minDate={values.effectiveFrom}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            label="Variable Name"
                                            name="variableName"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.variableName}
                                            error={
                                                !!touched.variableName &&
                                                !!errors.variableName
                                            }
                                            helperText={
                                                touched.variableName &&
                                                errors.variableName
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            label="Sort Order"
                                            name="sortOrder"
                                            type="number"
                                            fullWidth
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.sortOrder}                                            
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        "& input": { textAlign: "right" },
                                                    },
                                                },
                                            }}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <FormControlLabel
                                            sx={{ gridColumn: 'span 6' }}
                                            control={
                                                <Checkbox
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                    name="isActive"
                                                    color="primary"
                                                />
                                            }
                                            label="Is Active"
                                        />

                                        <Typography variant="h6" fontWeight="bold" sx={{ gridColumn: 'span 12' }}>
                                            Applicable To
                                        </Typography>                                        
                                        <Box sx={{ gridColumn: 'span 12', ml: 2 }}>
                                            <ApplicableTo
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                jobGrades={jobGrades}
                                            />
                                        </Box>

                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {travelAllowanceId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default TravelAllowancePage;